import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import {
    ChevronLeft,
    ChevronRight,
    Database,
    FileText,
    Home,
    Package,
    Settings,
    Truck,
    UserCog,
    X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';

const navigationItems = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: Home,
    roles: ['SUPERADMIN', 'MANAGER', 'DIRECTOR', 'SUPERVISOR', 'OPERATION']
  },
  {
    name: 'AWB Management',
    href: '/awb',
    icon: Package,
    roles: ['SUPERADMIN', 'DATA_ENTRY', 'OPERATION', 'CUSTOMER_SERVICES']
  },
  {
    name: 'Master Data',
    href: '/master-data',
    icon: Database,
    roles: ['SUPERADMIN', 'MANAGER', 'DATA_ENTRY']
  },
  {
    name: 'Manifests',
    href: '/manifests',
    icon: FileText,
    roles: ['SUPERADMIN', 'OPERATION', 'SUPERVISOR']
  },
  {
    name: 'Tracking',
    href: '/tracking',
    icon: Truck,
    roles: ['SUPERADMIN', 'CUSTOMER_SERVICES', 'OPERATION', 'DELIVERY_BOY']
  },
  // TODO: Uncomment when Billing feature is ready
  // {
  //   name: 'Billing',
  //   href: '/billing',
  //   icon: FileText,
  //   roles: ['SUPERADMIN', 'BILLING', 'MANAGER']
  // },
  // TODO: Uncomment when Reports feature is ready
  // {
  //   name: 'Reports',
  //   href: '/reports',
  //   icon: BarChart3,
  //   roles: ['SUPERADMIN', 'MANAGER', 'DIRECTOR', 'BILLING']
  // },
  {
    name: 'User Management',
    href: '/users',
    icon: UserCog,
    roles: ['SUPERADMIN', 'MANAGER', 'DIRECTOR']
  },
  {
    name: 'Settings',
    href: '/settings',
    icon: Settings,
    roles: ['SUPERADMIN', 'MANAGER']
  }
];

interface SidebarProps {
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
  isMobileOpen: boolean;
  setIsMobileOpen: (open: boolean) => void;
}

const Sidebar = ({ isCollapsed, setIsCollapsed, isMobileOpen, setIsMobileOpen }: SidebarProps) => {
  const { user } = useAuth();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const navItemRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const filteredNavigation = navigationItems.filter(item => 
    item.roles.includes(user?.role || '')
  );

  // Check if we're on mobile and handle responsive behavior
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      
      // Auto-collapse on tablet sizes
      if (window.innerWidth < 1280 && window.innerWidth >= 1024) {
        setIsCollapsed(true);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [setIsCollapsed]);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname, setIsMobileOpen]);

  // Persist collapse state in localStorage
  useEffect(() => {
    const savedState = localStorage.getItem('sidebar-collapsed');
    if (savedState !== null && !isMobile) {
      setIsCollapsed(JSON.parse(savedState));
    }
  }, [isMobile, setIsCollapsed]);

  const handleCollapseToggle = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem('sidebar-collapsed', JSON.stringify(newState));
  };

  // Keyboard navigation handler
  const handleKeyDown = (event: React.KeyboardEvent, action: () => void) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };

  const handleMouseEnter = (itemName: string, event: React.MouseEvent<HTMLElement>) => {
    if (isCollapsed && !isMobile) {
      const rect = event.currentTarget.getBoundingClientRect();
      setTooltipPosition({
        x: rect.right + 8, // 8px gap from sidebar
        y: rect.top + rect.height / 2 // Center vertically
      });
      setHoveredItem(itemName);
    }
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
    setTooltipPosition(null);
  };

  // Close tooltip on scroll to prevent positioning issues
  useEffect(() => {
    const handleScroll = () => {
      if (hoveredItem) {
        setHoveredItem(null);
        setTooltipPosition(null);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hoveredItem]);

  const SidebarContent = () => (
    <div className="flex h-full flex-col bg-background border-r">
      {/* Logo Section - Match Header Height */}
      <div className="flex items-center justify-between h-16 px-4 border-b bg-background/50">
        <div className={cn(
          "flex items-center transition-all duration-300 ease-in-out",
          isCollapsed && !isMobile ? "justify-center" : ""
        )}>
          {isCollapsed && !isMobile ? (
            <img
              src="/logo-icon.png"
              alt="Sairaj International"
              className="w-8 h-8 object-contain"
            />
          ) : (
            <img
              src="/logo.png"
              alt="Sairaj International"
              className="h-10 w-auto object-contain"
            />
          )}
        </div>
        
        {/* Mobile close button */}
        <button
          onClick={() => setIsMobileOpen(false)}
          className="lg:hidden p-2 rounded-md hover:bg-accent transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
          aria-label="Close menu"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {filteredNavigation.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.href || 
            (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
          
          return (
            <div key={item.name} className="relative">
              <NavLink
                to={item.href}
                onClick={() => isMobile && setIsMobileOpen(false)}
                onMouseEnter={(e) => handleMouseEnter(item.name, e)}
                onMouseLeave={handleMouseLeave}
                onKeyDown={(e) => handleKeyDown(e, () => {})}
                className={cn(
                  'flex items-center rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 group relative focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent',
                  isCollapsed && !isMobile 
                    ? 'justify-center px-2 py-3' 
                    : 'gap-3'
                )}
                aria-label={item.name}
                title={isCollapsed && !isMobile ? item.name : undefined}
              >
                <Icon className={cn(
                  "flex-shrink-0 transition-all duration-200",
                  isCollapsed && !isMobile 
                    ? "h-6 w-6" 
                    : "h-5 w-5"
                )} />
                
                <span className={cn(
                  "transition-all duration-300 ease-in-out overflow-hidden whitespace-nowrap",
                  isCollapsed && !isMobile 
                    ? "w-0 opacity-0" 
                    : "w-auto opacity-100"
                )}>
                  {item.name}
                </span>
              </NavLink>
            </div>
          );
        })}
      </nav>

      {/* Collapse toggle for desktop */}
      {!isMobile && (
        <div className="p-3 border-t bg-background/50">
          <button
            onClick={handleCollapseToggle}
            onKeyDown={(e) => handleKeyDown(e, handleCollapseToggle)}
            className={cn(
              "flex items-center w-full rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 group",
              isCollapsed ? "justify-center px-2" : "gap-3"
            )}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <ChevronRight className="h-5 w-5 transition-transform group-hover:scale-110" />
            ) : (
              <>
                <ChevronLeft className="h-5 w-5 transition-transform group-hover:scale-110" />
                <span className="transition-all duration-300 ease-in-out">
                  Collapse
                </span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );

  // Tooltip Portal Component
  const TooltipPortal = () => {
    if (!hoveredItem || !tooltipPosition || !isCollapsed || isMobile) {
      return null;
    }

    return createPortal(
      <div 
        className="fixed pointer-events-none z-[100] transition-all duration-200 ease-out"
        style={{
          left: tooltipPosition.x,
          top: tooltipPosition.y,
          transform: 'translateY(-50%)',
          opacity: hoveredItem ? 1 : 0,
          scale: hoveredItem ? 1 : 0.95
        }}
      >
        <div className="bg-popover text-popover-foreground border rounded-lg px-3 py-2 shadow-lg whitespace-nowrap text-sm font-medium backdrop-blur-sm">
          {hoveredItem}
          {/* Arrow pointing to the left */}
          <div className="absolute right-full top-1/2 transform -translate-y-1/2 translate-x-[1px]">
            <div className="w-0 h-0 border-t-4 border-b-4 border-r-4 border-transparent border-r-popover drop-shadow-sm" />
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <aside 
        className={cn(
          "hidden lg:flex fixed left-0 top-0 z-40 h-screen transition-all duration-300 ease-in-out will-change-transform",
          isCollapsed ? "w-16" : "w-64"
        )}
        aria-label="Main navigation"
      >
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar */}
      {isMobileOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="lg:hidden fixed inset-0 z-40 bg-black/20 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => setIsMobileOpen(false)}
            aria-label="Close sidebar"
          />
          
          {/* Mobile sidebar panel */}
          <aside 
            className="lg:hidden fixed left-0 top-0 z-50 h-full w-72 transform transition-transform duration-300 ease-in-out shadow-2xl"
            aria-label="Main navigation"
          >
            <SidebarContent />
          </aside>
        </>
      )}

      {/* Tooltip Portal */}
      <TooltipPortal />
    </>
  );
};

export default Sidebar;